import React, { useEffect, useState, ReactNode } from 'react'
import {
  Checkbox,
  Chip,
  Divider,
  List,
  ListItemButton,
  Popover,
  Stack,
  Typography,
  Theme
} from '@mui/material'
import { KeyboardArrowDown } from '@mui/icons-material'
import { useTranslation } from '@contexts/translation'

interface ChipOption {
  label: string
  handleClick: (selectedIds?: Array<string | number>) => void
  id?: string | number
  disabled?: boolean
  divider?: boolean
  tab?: boolean
}

interface FilterActionsSelectorChipProps {
  label?: string | null
  customLabel?: ReactNode | null
  options?: ChipOption[]
  disabled?: boolean
  defaultIndex?: number
  multiple?: boolean
  className?: string | null
  deleteIconColor?: string | null
  withLabel?: boolean
  size?: 'small' | 'medium' | 'normal'
  icon?: ReactNode
  height?: string
  borderRadius?: string
}

const FilterActionsSelectorChip = ({
  label = null,
  customLabel = null,
  options = [],
  disabled = false,
  defaultIndex = 0,
  multiple = false,
  className = null,
  deleteIconColor = null,
  withLabel = false,
  size = 'normal',
  icon,
  height = '32px',
  borderRadius = '8px'
}: FilterActionsSelectorChipProps): JSX.Element => {
  const { dictionary } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | number[]>(
    multiple ? [] : defaultIndex
  )

  useEffect(() => {
    if (!multiple) setSelectedIndex(defaultIndex)
  }, [defaultIndex, multiple])

  const _handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const _handleClickItem = (option: ChipOption, idx: number): void => {
    setSelectedIndex(idx)
    option.handleClick()
    setAnchorEl(null)
  }

  const _handleClickItemMultiple = (option: ChipOption, idx: number): void => {
    let selectedOptions: number[] = []
    const currentSelected = selectedIndex as number[]
    if (idx === 0) {
      if (currentSelected.length < options.length) {
        selectedOptions = Array.from({ length: options.length }, (_, i) => i)
        setSelectedIndex(selectedOptions)
      } else setSelectedIndex([])
    } else {
      if (currentSelected.includes(idx)) {
        selectedOptions = currentSelected.filter(i => i !== idx)
        setSelectedIndex(selectedOptions)
      } else {
        selectedOptions = [...currentSelected, idx]
        setSelectedIndex(selectedOptions)
      }
    }
    const filtered = options.filter((o, i) => selectedOptions.includes(i))
    const ids = filtered
      .map(f => f.id)
      .filter((id): id is string | number => id !== undefined)
    option.handleClick(ids)
  }

  const _handleClose = (): void => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'more-actions-popover' : undefined

  return (
    <div>
      <Chip
        size={size === 'normal' ? 'medium' : size}
        disabled={disabled}
        aria-describedby={id}
        onClick={_handleClick}
        sx={{
          backgroundColor: 'white',
          color: (theme: Theme) => theme.palette.text.primary,
          borderRadius: borderRadius,
          border: '1px solid #E0E0E0',
          height: height
        }}
        className={className || ''}
        label={
          customLabel ? (
            customLabel
          ) : (
            <Stack direction="row" alignItems="center" gap={1}>
              {icon}
              <Stack
                direction="row"
                alignItems="center"
                flexWrap="nowrap"
                spacing={1}
                fontSize={13}
              >
                {multiple &&
                  Array.isArray(selectedIndex) &&
                  selectedIndex.length > 0 &&
                  selectedIndex.length < options.length &&
                  `${label}: ${selectedIndex.length} ${dictionary.selected}`}
                {multiple &&
                  Array.isArray(selectedIndex) &&
                  selectedIndex.length >= options.length &&
                  `${label}: ${dictionary.all} ${dictionary.selected}`}
                {multiple &&
                  Array.isArray(selectedIndex) &&
                  !selectedIndex.length &&
                  options[0]?.label}
                {!multiple &&
                  !withLabel &&
                  typeof selectedIndex === 'number' &&
                  (options[selectedIndex]?.label || label)}
                {!multiple &&
                  withLabel &&
                  typeof selectedIndex === 'number' &&
                  `${label}: ${options[selectedIndex]?.label}`}
              </Stack>
            </Stack>
          )
        }
        onDelete={_handleClick}
        deleteIcon={
          <KeyboardArrowDown
            aria-describedby={id}
            fontSize="small"
            sx={{ color: deleteIconColor || '' }}
          />
        }
        data-testid="Chip-214f4c6f-8319-4f46-9555-20cb67cc63c0"
      />

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={_handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <List>
          {options.map((option, idx) => (
            <React.Fragment key={`option-${idx}`}>
              <ListItemButton
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: '#F2BB2E14',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#F2BB2E1F'
                    }
                  }
                }}
                selected={
                  !Array.isArray(selectedIndex) && selectedIndex === idx
                }
                onClick={() =>
                  multiple
                    ? _handleClickItemMultiple(option, idx)
                    : _handleClickItem(option, idx)
                }
                disabled={disabled || option.disabled}
                data-testid="ListItemButton-9b84c503-93df-4815-ac3b-feb2bc2af58f"
              >
                {multiple && (
                  <Checkbox
                    checked={
                      Array.isArray(selectedIndex) &&
                      selectedIndex.includes(idx)
                    }
                    sx={{ mr: 1 }}
                    data-testid="Checkbox-57880975-61a8-41b9-a908-578684594ad1"
                  />
                )}

                <Typography
                  sx={{ ml: option.tab ? 2 : 0 }}
                  data-testid="Typography-7e2f4fee-a1a2-40bf-af56-bd00caf03161"
                >
                  {option.label}
                </Typography>
              </ListItemButton>
              {option.divider && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Popover>
    </div>
  )
}

export default FilterActionsSelectorChip
